import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import {
  InMemoryCache,
  defaultDataIdFromObject,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import types from '../../../../../constants/gql/fragmentTypes.json';
import authLink from './links/auth';
import stateLink from './links/state';
import errorLink from './links/error';
import { removeTypename } from '../utils';
import gql from 'graphql-tag';

const cache = new InMemoryCache({
  dataIdFromObject: x => {
    if (!x) {
      return;
    }

    return defaultDataIdFromObject(x);
  },
  fragmentMatcher: new IntrospectionFragmentMatcher({
    introspectionQueryResultData: types,
  }),
});

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = removeTypename(operation.variables);
  }

  return forward(operation);
});

const instantiateClient = link => {
  const client = new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        // Allow retrieving data from an error response.
        // https://github.com/apollographql/apollo-client/blob/master/docs/source/features/error-handling.md.
        errorPolicy: 'all',
      },
    },
  });

  client.hasBeenLoadedBefore = function() {
    const val = this.cache.readQuery({
      query: gql`
        query {
          hasBeenLoadedBefore
        }
      `,
    }).hasBeenLoadedBefore;

    if (!val) {
      this.cache.writeQuery({
        query: gql`
          query {
            hasBeenLoadedBefore
          }
        `,
        data: {
          hasBeenLoadedBefore: true,
        },
      });
    }

    return val;
  };

  client.moveId = function() {
    return this.cache.readQuery({
      query: gql`
        query {
          moveId
        }
      `,
    }).moveId;
  };

  client.requestHeader = function() {
    return this.cache.readQuery({
      query: gql`
        query {
          requestHeader {
            client
            accessToken
            uid
          }
        }
      `,
    }).requestHeader;
  };

  client.nextStepCards = function() {
    return JSON.parse(
      this.cache.readQuery({
        query: gql`
          query {
            nextStepCards
          }
        `,
      }).nextStepCards
    );
  };

  client.user = function() {
    const user = this.cache.readQuery({
      query: gql`
        query {
          user {
            email
            phone
          }
        }
      `,
    });
    return user.user;
  };
  return client;
};

const createClient = (
  { env, sessionHeaders },
  initialState = {},
  getRouter
) => {
  const httpLink = new HttpLink({
    uri: `${env.endpoints.api.url}/graphql`,
  });

  const link = authLink(sessionHeaders)
    .concat(stateLink(cache, initialState))
    .concat(omitTypenameLink)
    .concat(errorLink(getRouter));

  return Promise.resolve(instantiateClient(link.concat(httpLink)));
};

export default createClient;
